import { useEffect, useRef } from 'react';

import {
    capitalize,
    flattenObject,
    getDeviceType,
    getStorageValue,
} from 'App/Helpers';
import { UAParser } from 'ua-parser-js';

const LP_VERSION_EVENTS = {
    mobile: { design: 'LP:MB', gaAction: 'lp_mb' },
    desktop: { design: 'LP:DT', gaAction: 'lp_dt' },
};

const isDefined = (value) => value !== undefined && value !== null;

const sendLanguageInfo = (
    locale,
    sendGameDesignEvent,
    sendGoogleBasicEvent
) => {
    const lang = String(locale || '').toLowerCase();
    const pretty = capitalize(lang);

    sendGameDesignEvent(`Language:Load:${pretty}`);
    sendGoogleBasicEvent('language', `load_${lang}`);
};

const sendDeviceInfo = (
    lpVersion,
    sendGameDesignEvent,
    sendGoogleBasicEvent,
    sendGoogleEvent
) => {
    const parser = new UAParser();
    const deviceInfo = parser.getResult();
    const deviceType = capitalize(getDeviceType());
    const {
        browser: { name, version },
        device: { model, vendor },
    } = deviceInfo;
    const versionEvent = LP_VERSION_EVENTS[lpVersion];

    if (versionEvent) {
        sendGameDesignEvent(versionEvent.design);
        sendGoogleBasicEvent('lp', versionEvent.gaAction);
    }

    sendGameDesignEvent(`Device:${deviceType}:${vendor}:${model}`);
    sendGameDesignEvent(`Browser:${name}:${version}:${deviceType}`);
    sendGoogleEvent(
        'device_info',
        flattenObject({
            ...deviceInfo,
            device: { ...deviceInfo.device, type: deviceType },
        })
    );
};

const sendPwaSupport = (
    pwaStatus,
    sendGameDesignEvent,
    sendGoogleBasicEvent
) => {
    if (pwaStatus === 'NotSupported') {
        sendGameDesignEvent('PWA:NotSupported');
        sendGoogleBasicEvent('pwa', 'not_supported');
        return;
    }

    if (pwaStatus !== 'Installed') return;

    if (window.matchMedia('(display-mode: standalone)').matches) {
        sendGameDesignEvent('PWA:OpenedStandalone');
        sendGoogleBasicEvent('pwa', 'opened_standalone');
    }
    if (window.matchMedia('(display-mode: browser)').matches) {
        sendGameDesignEvent('PWA:OpenedBrowser');
        sendGoogleBasicEvent('pwa', 'opened_browser');
    }
};

const sendPushSupport = (
    pushStatus,
    storageKey,
    sendGameDesignEvent,
    sendGoogleBasicEvent
) => {
    const prompt = getStorageValue(storageKey, 'push.brwPromptCount');

    if (pushStatus === 'notAvailable') {
        sendGameDesignEvent('Push:NotAvailable');
        sendGoogleBasicEvent('push', 'not_available');
        return;
    }

    if (prompt) return;

    if (Notification?.permission === 'granted') {
        sendGameDesignEvent('Push:GrantedByBrowser');
        sendGoogleBasicEvent('push', 'granted_by_browser');
    }
    if (Notification?.permission === 'denied') {
        sendGameDesignEvent('Push:DeniedByBrowser');
        sendGoogleBasicEvent('push', 'denied_by_browser');
    }
};

/**
 * Sends a one-shot landing snapshot when language / PWA / push data is
 * available and analytics consent is granted: device/browser, PWA display
 * mode, push permission, and optional LP version (`LP:MB` / `LP:DT`). Uses the
 * provider's deferred send helpers, so calls wait in the queue while an
 * approved client initializes and are dropped if initialization fails.
 *
 * @param {object} options
 * @param {Function} options.sendGameDesignEvent
 * @param {Function} options.sendGoogleBasicEvent
 * @param {Function} options.sendGoogleEvent
 * @param {string} [options.locale]
 * @param {string} [options.pwaStatus]
 * @param {string} [options.pushStatus]
 * @param {string} options.storageKey - Storage namespace for push prompt count.
 * @param {'main'|'mobile'|'desktop'} [options.lpVersion='main'] -
 *   Mobile/desktop emit an extra LP version event; main does not.
 * @param {boolean} [options.analyticsConsent] - Analytics consent state.
 */
export const useLandingAnalytics = ({
    sendGameDesignEvent,
    sendGoogleBasicEvent,
    sendGoogleEvent,
    locale,
    pwaStatus,
    pushStatus,
    storageKey,
    lpVersion = 'main',
    analyticsConsent,
}) => {
    const ran = useRef({ deviceLang: false, pwa: false, push: false });

    useEffect(() => {
        if (
            analyticsConsent !== true ||
            !isDefined(locale) ||
            ran.current.deviceLang
        )
            return;

        sendLanguageInfo(locale, sendGameDesignEvent, sendGoogleBasicEvent);
        sendDeviceInfo(
            lpVersion,
            sendGameDesignEvent,
            sendGoogleBasicEvent,
            sendGoogleEvent
        );
        ran.current.deviceLang = true;
    }, [
        analyticsConsent,
        locale,
        lpVersion,
        sendGameDesignEvent,
        sendGoogleBasicEvent,
        sendGoogleEvent,
    ]);

    useEffect(() => {
        if (
            analyticsConsent !== true ||
            !isDefined(pwaStatus) ||
            ran.current.pwa
        )
            return;

        sendPwaSupport(pwaStatus, sendGameDesignEvent, sendGoogleBasicEvent);
        ran.current.pwa = true;
    }, [
        analyticsConsent,
        pwaStatus,
        sendGameDesignEvent,
        sendGoogleBasicEvent,
    ]);

    useEffect(() => {
        if (
            analyticsConsent !== true ||
            !isDefined(pushStatus) ||
            ran.current.push
        )
            return;

        sendPushSupport(
            pushStatus,
            storageKey,
            sendGameDesignEvent,
            sendGoogleBasicEvent
        );
        ran.current.push = true;
    }, [
        analyticsConsent,
        pushStatus,
        storageKey,
        sendGameDesignEvent,
        sendGoogleBasicEvent,
    ]);
};
